'use client';
import {
  SearchAnalyticsBoundary,
  SearchAnalyticsItem,
  type SearchAnalyticsData,
} from '@eliadmin/core/src/components/analytics/search-analytics-boundary';
import { SearchSort } from '@eliadmin/core/src/components/header/search-bar/search-sort';
import { searchSortOptions } from '@eliadmin/core/src/utils/search-sort';

import { buildProductsRequest } from '@eliadmin/core/src/api/products/product-list-request';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchProducts } from '@eliadmin/core/src/api/products/product-api';

import { PageHeader } from '@eliadmin/core/src/components/homepage/page-header';
import { TrackComponent } from '@eliadmin/core/src/components/analytics/analytics-helpers';
import { ProductCard } from '@eliadmin/core/src/components/product/product-card';
import { ProductListSchema } from '@eliadmin/core/src/components/seo/product-list-schema';
import { Container } from '@eliadmin/core/src/components/ui/container';
import { Pagination } from '@eliadmin/core/src/components/ui/pagination';
import { Spinner } from '@eliadmin/core/src/components/ui/spinner';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import type { SearchFields } from '@eliadmin/core/src/utils/product-utils';
import {
  buildProductsQuery,
  parseProductSearchParams,
} from '@eliadmin/core/src/utils/product-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useQuery } from '@tanstack/react-query';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { Suspense, useMemo, useState, useEffect } from 'react';
import { CustomSearchDialog } from '@eliadmin/core/src/components/header/search-bar/custom-search-dialog';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { DEFAULT_SEARCH_FIELDS } from '@eliadmin/core/src/components/header/search-bar/custom-search-config';

export default function SearchPage() {
  const locale = useLocaleStore((s) => s.currentLocale);
  const { t } = useTranslations();
  const { me, isInitialized } = useCustomerStore();
  const templateSettings = useGlobalStore((s) => s.templateSettings);
  const currency = useGlobalStore((s) => s.generalLayout?.currency);

  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const parsed = useMemo(() => parseProductSearchParams(searchParams), [searchParams]);

  const [usedCustomSearchFields, setUsedCustomSearchFields] = useState<SearchFields | undefined>(
    () => parsed.search_fields ?? DEFAULT_SEARCH_FIELDS,
  );

  // Keep local "used" fields in sync with URL
  useEffect(() => {
    // When URL contains explicit search_fields, prefer them.
    // Otherwise fall back to configured defaults.
    if (parsed.search_fields) {
      setUsedCustomSearchFields(parsed.search_fields);
    } else {
      setUsedCustomSearchFields(DEFAULT_SEARCH_FIELDS);
    }
  }, [parsed.search_fields]);

  // legacy sync function removed; handled directly in onApply

  const query = buildProductsQuery({
    ...parsed,
    search_fields: usedCustomSearchFields ?? parsed.search_fields,
    results: 12,
    showAttributes: true,
    variant_images: true,
    ...searchSortOptions(searchParams),
  });

  const productRequestUri = buildProductsRequest(query).uri;
  const productResetUri = buildProductsRequest({ ...query, page: undefined }).uri;

  const {
    data: products,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: [CacheKey.PRODUCTS, locale, productRequestUri, me?.customer.id ?? null],
    queryFn: ({ signal }) =>
      fetchProducts({
        locale,
        query,
        authToken: me?.authToken,
        signal,
      }),
    enabled: isInitialized,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    staleTime: 1000 * 60 * 5,
  });

  return (
    <div>
      <PageHeader
        heading={t('text211')}
        description={
          searchParams.get('search') ? `${t('text211')}: ${searchParams.get('search')}` : ''
        }
        afterHeading={
          templateSettings?.search_page?.custom_search?.enabled && (
            <CustomSearchDialog
              activeState={(() => {
                const fields = usedCustomSearchFields ?? DEFAULT_SEARCH_FIELDS;
                const enabled = Object.values(fields).some(Boolean);
                return {
                  enabled,
                  name: !!fields.name,
                  ['name.partial']: !!fields['name.partial'],
                  description: !!fields.description,
                  short_description: !!fields.short_description,
                  code: !!fields.code,
                  ean: !!fields.ean,
                  ext_code: !!fields.ext_code,
                };
              })()}
              isApplying={isFetching}
              onApply={(next, localSearch) => {
                const nextFields: SearchFields | undefined = next.enabled
                  ? ({
                      name: next.name,
                      ['name.partial']: next['name.partial'],
                      description: next.description,
                      short_description: next.short_description,
                      code: next.code,
                      ean: next.ean,
                      ext_code: next.ext_code,
                    } as SearchFields)
                  : undefined;
                setUsedCustomSearchFields(nextFields);
                const params = new URLSearchParams(searchParams.toString());
                params.delete('page');
                params.delete('search_fields');
                params.delete('search_fields[]');
                if (nextFields) {
                  if (nextFields.name) params.append('search_fields[]', 'name');
                  if (nextFields['name.partial']) params.append('search_fields[]', 'name.partial');
                  if (nextFields.description) params.append('search_fields[]', 'description');
                  if (nextFields.short_description)
                    params.append('search_fields[]', 'short_description');
                  if (nextFields.code) params.append('search_fields[]', 'code');
                  if (nextFields.ean) params.append('search_fields[]', 'ean');
                  if (nextFields.ext_code) params.append('search_fields[]', 'ext_code');
                }
                if (!localSearch) {
                  params.delete('search');
                } else {
                  params.set('search', localSearch);
                }
                router.replace(`${pathname}?${params.toString()}`, { scroll: false });
              }}
            />
          )
        }
        className={{ wrapper: 'bg-transparent' }}
      />
      {/* Custom search moved to dialog; removed outside container */}

      <div className="hidden">
        <div className="hidden w-full justify-end px-4 sm:flex">
          <></>
        </div>
        <div className="flex w-full justify-end px-4 pt-2 sm:hidden">
          <></>
        </div>
      </div>
      <Container className={cn('flex flex-col space-y-5 py-8')}>
        {/* <h3 className={cn('mb-4 text-3xl font-bold')}>{t('text1')}</h3> */}
        <div className="flex flex-col items-center gap-4">
          {isLoading ? (
            <div className={cn('flex h-[300px] w-full items-center justify-center')}>
              <Spinner />
            </div>
          ) : (
            <>
              {!!products?.data?.length && (
                <ProductListSchema products={products.data} currency={currency} />
              )}
              {!!products?.data?.length && (
                <TrackComponent
                  eventName={'view_item_list'}
                  eventParams={{
                    item_list_id: `search:${searchParams.get('search') ?? ''}`,
                    item_list_name: 'search',
                  }}
                  items={products.data}
                />
              )}
              <>
                <SearchSort />
                <SearchAnalyticsBoundary
                  mode="submitted"
                  active={!isFetching}
                  data={{
                    manifest: products?.meta?.search,
                    identity: JSON.stringify([
                      locale,
                      buildProductsRequest(query).uri,
                      me?.customer.id ?? null,
                    ]),
                    locale,
                    results: (products?.data ?? []).map((product) => ({
                      type: 'product',
                      id: product.id,
                      href: product.slug,
                    })),
                    offset: products?.meta?.from
                      ? products.meta.from - 1
                      : (Number(query.page ?? 1) - 1) * Number(query.results ?? 12),
                  }}
                >
                  <div
                    className={cn(
                      'grid w-full grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4',
                    )}
                  >
                    {products?.data.map((product, searchIndex) => (
                      <SearchAnalyticsItem index={searchIndex} key={searchIndex}>
                        {<ProductCard key={`product-${product.id}`} product={product} />}
                      </SearchAnalyticsItem>
                    ))}
                  </div>
                </SearchAnalyticsBoundary>
              </>
            </>
          )}
          <Suspense>
            <Pagination links={products?.meta?.links ?? []} resetWhen={[productResetUri]} />
          </Suspense>
        </div>
      </Container>
    </div>
  );
}
